import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { Box, Grid } from '@material-ui/core';
import { Dimmer } from 'semantic-ui-react';
import useForm from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import Wizard from '../../components/Wizard';
import { STEP_IDS, steps } from './recurringSteps';
import {
  getSavedCards,
  makeBindingPayment,
  pay,
  addPayerAndSaveInCache,
} from '../../redux/modules/payment';
import { clearContract } from '../../redux/modules/recPayment';
import { clearBasket } from '../../redux/modules/basketReducer';
import EfesContentHeader from '../../components/EfesContentHeader';
import PolicySummary from '../../components/PolicySummary';
import Loader from '../../components/Loaders/Loader';
import useStepNavigationAmountStyles from '../../components/MUI/StepNavigationAmount/makeStyles';
import ScrollToTopPage from '../../components/ScrollToTopPage';
import { payerContactInfoSchema } from '../../validations/payerContactInfoSchema';
import useDeviceResize from '../../hooks/useDeviceResize';
import { EFES_CONTENT_WIDTH, EFES_SUMMARY_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import { AMD_SYMBOL } from '../../constants';
import { RECURRING_PAYMENT_PATH } from '../../constants/routes';

import cardIcon from '../../img/svgs/payment/contentCard.svg';

const getUnlockedSteps = (recovering) => {
  if (recovering) {
    return [STEP_IDS.SCHEDULE, STEP_IDS.PAYMENT];
  }
  return [STEP_IDS.SCHEDULE];
};

const MakeRecurringPaymentContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isDeviceLgSize = useDeviceResize('lg');
  const stepNavigationAmountClasses = useStepNavigationAmountStyles();
  const isAuthed = useSelector((state) => state.auth.isAuthenticated);
  const { user } = useSelector((state) => state.userReducer, shallowEqual);
  const basket = useSelector((store) => store.basketReducer.basket, shallowEqual);
  const [stepNavigationBox, setStepNavigationBox] = useState({});

  const generalAmount = basket.reduce((acc, curr) => acc + curr.totalAndBalance.selectedToPay, 0);

  const payBtnAmount = useMemo(
    () => (generalAmount ? ` ${AMD_SYMBOL} ${generalAmount.toLocaleString('en-US')}` : ''),
    [generalAmount],
  );

  const recovering = useSelector((store) => store.paymentReducer.recoveringRecurringPayment);
  const { recurringPaymentMethod, savingRecurringPayment, orderId } = useSelector(
    (store) => store.paymentReducer,
    shallowEqual,
  );
  const { saveCard } = useSelector((store) => store.paymentReducer);
  const [isPaymentNextValid, setIsPaymentNextValid] = useState(true);

  const [urlParams] = useState(queryString.parse(location.hash));
  const [openMobileSummary, setOpenMobileSummary] = useState();
  const [currentStep, setCurrentStep] = useState(steps[0]);
  const [unlockedSteps, setUnlockedSteps] = useState(getUnlockedSteps(urlParams.bfp === 'true'));

  const { register, handleSubmit, errors, setValue, triggerValidation, formState } = useForm({
    validationSchema: payerContactInfoSchema,
    mode: 'onBlur',
  });

  useEffect(() => {
    if (isAuthed) {
      dispatch(getSavedCards());
    }
  }, [dispatch, isAuthed]);

  const payer = useMemo(() => {
    if (isAuthed) {
      const { email, firstName, lastName, cellPhone, id } = user;
      return {
        email,
        emailConfirm: email,
        firstName,
        lastName,
        mobilePhone: cellPhone,
        userId: id,
      };
    }
    return {};
  }, [isAuthed, user]);

  useEffect(() => {
    Object.keys(payer).forEach((key) => {
      register({ name: key });
    });
  }, [payer, register]);

  useEffect(() => {
    Object.keys(payer).forEach((key) => {
      setValue(key, payer[key]);
    });
  }, [payer, setValue]);

  useEffect(() => {
    if (recurringPaymentMethod) {
      setIsPaymentNextValid(true);
    }
  }, [recurringPaymentMethod]);

  const handleNext = useCallback(
    (shouldSaveCard = false) => {
      if (!recurringPaymentMethod) {
        setIsPaymentNextValid(false);
      } else if (recurringPaymentMethod.savedCard && orderId) {
        dispatch(makeBindingPayment(orderId, recurringPaymentMethod.savedCard));
      } else if (!recurringPaymentMethod.savedCard && !savingRecurringPayment) {
        dispatch(pay(shouldSaveCard));
      }
    },
    [dispatch, orderId, recurringPaymentMethod, savingRecurringPayment],
  );

  const onSubmit = useCallback(
    (data) => {
      dispatch(addPayerAndSaveInCache(data))
        .then(() => {
          handleNext(saveCard);
        })
        .catch(() => {});
    },
    [dispatch, handleNext, saveCard],
  );

  const handleOpenMobileSummary = useCallback(() => {
    setOpenMobileSummary(!openMobileSummary);
  }, [openMobileSummary]);

  const handlePayClick = useCallback(() => {
    if (currentStep.id === STEP_IDS.SCHEDULE) {
      history.push({
        pathname: RECURRING_PAYMENT_PATH,
      });
      setCurrentStep(steps[1]);
      setUnlockedSteps([STEP_IDS.SCHEDULE, STEP_IDS.PAYMENT]);
    } else if (currentStep.id === STEP_IDS.PAYMENT) {
      handleSubmit(onSubmit)();
    }
  }, [history, currentStep, handleSubmit, onSubmit, setCurrentStep]);

  const stepParams = {
    payerContactFormProps: { register, errors, setValue, triggerValidation, formState },
    isPaymentNextValid,
    openMobileSummary,
    handleOpenMobileSummary,
    setStepNavigationBox,
    generalAmount,
    handlePayClick,
    payBtnTitle: t('pay'),
    payBtnAmount,
  };

  useEffect(() => () => {
    dispatch(clearContract());
    dispatch(clearBasket());
    setValue('policyNumber', '');
  },
  [dispatch, setValue]);

  return (
    <>
      <ScrollToTopPage />
      <EfesContentHeader
        headerText="recurrPayment.headerTitle"
        headerImage={cardIcon}
        imageWidth={63}
      />

      {recovering && (
        <Dimmer active inverted>
          <Loader />
        </Dimmer>
      )}

      <Grid container>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH,
            paddingRight: isDeviceLgSize ? 0 : 16,
          }}
        >
          <Wizard
            stepContainers={steps}
            stepParams={stepParams}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            unlockedSteps={unlockedSteps}
            setUnlockedSteps={setUnlockedSteps}
          />
        </Grid>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_SUMMARY_WIDTH,
            paddingLeft: isDeviceLgSize ? 0 : 16,
          }}
        >
          <PolicySummary
            openMobileSummary={openMobileSummary}
            onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
            handlePayClick={handlePayClick}
          />
        </Grid>
      </Grid>
      {isDeviceLgSize && (
        <Grid
          container
          className={cn({
            [stepNavigationAmountClasses.mobileNavigation]: isDeviceLgSize,
          })}
          spacing={isDeviceLgSize ? 4 : 0}
        >
          <Grid item style={{ width: FULL_WIDTH }}>
            <Box>{!isEmpty(stepNavigationBox) && stepNavigationBox}</Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default MakeRecurringPaymentContainer;
